import voca from "voca";
import moment from "moment";

import { UserAttributes } from "../attributes/user-attributes";
import UserDetail from "./UserDetail";
import Country from "./Country";
import Business from "./Business";
import BusinessBranch from "./BusinessBranch";
import { parseDate } from "../common/Hooks";
import { BusinessAttributes } from "../attributes/business-attributes";
import { BusinessBranchAttributes } from "../attributes/business-branch-attributes";
import ConnectionLog from "./ConnectionLog";
import BaseModel from "./BaseModel";
import { MemberStatus } from "../settings/MemberStatus";
import Document from "./Document";
import Account from "./Account";
import BusinessAccount from "./BusinessAccount";
import { DocumentAttributes } from "../attributes/document-attributes";
import { AccountAttributes } from "../attributes/account-attributes";
import { DD_MM_YYYY_FORMAT, YYYY_MM_DD_FORMAT } from "../settings/constants";
import BranchManager from "./BranchManager";
import { BranchManagerAttributes } from "../attributes/branch-manager-attributes";
import AdminRole from "./AdminRole";
import {
  AdminRoleAttributes,
  AdminRoleEnum,
} from "../attributes/admin-role-attributes";
import Language from "./Language";
import { LanguageAttributes } from "../attributes/language-attributes";

export enum UserProfileType {
  personal = 1,
  businessOwner = 2,
  branchManager = 3,
  employee = 4,
  allinOne = 5,
  icipAdmin = 10,
}

export enum UserScopes {
  docs = "docs",
  accounts = "accounts",
}

class User extends BaseModel {
  id: number;
  login: string;
  user_account_type: number;
  pin_attempts_left: number;
  profile_picture: string | null;
  blocked_at: Date | null;
  validation_date: Date | null;
  declined_at: Date | null;
  requested_more_at: Date | null;
  currency_code: string;
  message: string;

  user_detail?: UserDetail;
  country?: Country;
  businesses?: Array<Business>;
  branches?: Array<BusinessBranch>;
  branch_managers?: Array<BranchManager>;
  connection_log?: ConnectionLog;
  documents?: Array<Document>;
  accounts?: Array<Account>;
  admin_roles?: Array<AdminRole>;
  language?: Language;

  constructor(
    id: number,
    login: string,
    user_account_type: number,
    pin_attempts_left: number,
    profile_picture: string | null,
    blocked_at: Date | null,
    validation_date: Date | null,
    created_at: Date | null,
    requested_more_at: Date | null,
    declined_at: Date | null,
    currency_code: string,
    message: string,
    user_detail: UserDetail,
    country: Country,
    businesses: Array<Business>,
    branches: Array<BusinessBranch>,
    connectionLog?: ConnectionLog,
    documents?: Array<Document>,
    accounts?: Array<Account>,
    branch_managers?: Array<BranchManager>,
    admin_roles?: Array<AdminRole>,
    language?: Language
  ) {
    super(created_at);

    this.id = id;
    this.login = login;
    this.user_account_type = user_account_type;
    this.pin_attempts_left = pin_attempts_left;
    this.profile_picture = profile_picture;
    this.blocked_at = blocked_at;
    this.validation_date = validation_date;
    this.declined_at = declined_at;
    this.requested_more_at = requested_more_at;
    this.currency_code = currency_code;
    this.message = message;
    this.user_detail = user_detail;
    this.country = country;
    this.businesses = businesses;
    this.branches = branches;
    this.connection_log = connectionLog;
    this.documents = documents;
    this.accounts = accounts;
    this.branch_managers = branch_managers;
    this.admin_roles = admin_roles;
    this.language = language;
  }

  static fromJson(json: UserAttributes): User {
    if (json === null || json === undefined) {
      return null;
    }

    return new User(
      json.id,
      json.login,
      json.user_account_type,
      json.pin_attempts_left,
      json.avatar ? json.avatar : json.profile_picture,
      parseDate(json.blocked_at),
      parseDate(json.validation_date),
      parseDate(json.created_at),
      parseDate(json.requested_more_at),
      parseDate(json.declined_at),

      json.currency_code,
      json.message,
      UserDetail.fromJson(json.user_detail),
      Country.fromJson(json.country),
      User.jsonToBusinesses(json.businesses),
      User.jsonToBusinessBranches(json.branches),
      ConnectionLog.fromJson(json.connection_log),
      User.jsonToDocs(json.documents),
      User.jsonToAccounts(json.accounts),
      User.jsonToManagers(json.branch_managers),
      User.jsonToAdminRoles(json.admin_roles),
      User.jsonToLanguage(json.language)
    );
  }

  static jsonToLanguage(json: LanguageAttributes): Language {
    if (json === null || json === undefined) {
      return null;
    }

    return Language.fromJson(json);
  }

  static jsonToAccounts(
    json: Array<AccountAttributes>
  ): Array<BusinessAccount> {
    if (json === null || json === undefined) {
      return [];
    }

    return json.map((item) => Account.fromJson(item));
  }

  static jsonToDocs(json: Array<DocumentAttributes>): Array<Document> {
    if (json === null || json === undefined) {
      return [];
    }

    return json.map((item) => Document.fromJson(item));
  }

  static jsonToBusinesses(json: Array<BusinessAttributes>): Array<Business> {
    if (json === null || json === undefined) {
      return [];
    }

    return json.map((item) => Business.fromJson(item));
  }

  static jsonToBusinessBranches(
    json: Array<BusinessBranchAttributes>
  ): Array<BusinessBranch> {
    if (json === null || json === undefined) {
      return [];
    }

    return json.map((item) => BusinessBranch.fromJson(item));
  }

  static jsonToManagers(
    json: Array<BranchManagerAttributes>
  ): Array<BranchManager> {
    if (json === null || json === undefined) {
      return [];
    }

    return json.map((item) => BranchManager.fromJson(item));
  }

  static jsonToAdminRoles(json: Array<AdminRoleAttributes>): Array<AdminRole> {
    if (json === null || json === undefined) {
      return [];
    }

    return json.map((item) => AdminRole.fromJson(item));
  }

  currencyCode(): string {
    return this.currency_code.toString().toUpperCase();
  }

  firstName(): string {
    if (this.user_detail !== null) {
      return voca.titleCase(this.user_detail.first_name);
    }

    return "";
  }

  fullName(): string {
    if (this.user_detail !== null) {
      return voca.titleCase(
        this.user_detail.last_name + "  " + this.user_detail.first_name
      );
    }

    return "";
  }

  countryStr(): any {
    if (this.country !== null) {
      return this.country.designation;
    }

    return "";
  }

  email(): string {
    if (this.user_detail !== null) {
      return this.user_detail.email;
    }

    return "";
  }

  avatar(): any {
    if (!this.profile_picture) {
      return process.env.PUBLIC_URL + "/user_avatar.png";
    }

    return this.profile_picture;
  }

  birthPlace(): string {
    if (this.user_detail !== null) {
      return this.user_detail.birth_place;
    }

    return "";
  }

  telephone(): string {
    if (this.user_detail !== null) {
      return this.user_detail.telephone;
    }

    return "";
  }

  otherPhoneNumber(): string {
    if (this.user_detail !== null) {
      return this.user_detail.secondary_telephone;
    }

    return "";
  }

  dateOfBirth = (): string => {
    if (this.user_detail !== null && this.user_detail.data_of_birth !== null) {
      return moment(this.user_detail.data_of_birth, YYYY_MM_DD_FORMAT).format(
        DD_MM_YYYY_FORMAT
      );
    }

    return "";
  };

  address(): string {
    if (this.user_detail !== null) {
      return this.user_detail.address;
    }

    return "";
  }

  getMessage(): string {
    return this.message;
  }

  category = (): string => {
    switch (this.user_account_type) {
      case UserProfileType.employee:
        return "sharedScreen.employee";
      case UserProfileType.businessOwner:
        return "sharedScreen.theBusinessOwner";
      case UserProfileType.branchManager:
        return "sharedScreen.branchManager";
      case UserProfileType.allinOne:
        return "sharedScreen.business";
      default:
        return null;
    }
  };

  status = (): MemberStatus => {
    if (this.requested_more_at !== null) {
      return MemberStatus.pending;
    }

    if (this.validation_date !== null) {
      return MemberStatus.approved;
    }

    if (this.declined_at !== null) {
      return MemberStatus.declined;
    }

    return MemberStatus.new;
  };

  accountStatus = (): MemberStatus | null => {
    if (this.blocked_at !== null) {
      return MemberStatus.locked;
    }

    return null;
  };

  isApproved(): boolean {
    return this.validation_date !== null;
  }

  isDeclined(): boolean {
    return this.declined_at !== null;
  }

  isPending(): boolean {
    return this.requested_more_at !== null;
  }

  isLocked(): boolean {
    return this.blocked_at !== null;
  }

  isAdmin(): boolean {
    return this.user_account_type === UserProfileType.icipAdmin;
  }

  isPersonal(): boolean {
    return this.user_account_type === UserProfileType.personal;
  }

  isOwner(): boolean {
    return this.user_account_type === UserProfileType.businessOwner;
  }

  isEmployee(): boolean {
    return this.user_account_type === UserProfileType.employee;
  }

  // Set

  setAccounts(accounts: Array<Account>): void {
    this.accounts = accounts;
  }

  setBlockedAt(value: Date | null): void {
    this.blocked_at = value;
  }

  hasAdminRole(roleValue: AdminRoleEnum): boolean {
    if (
      this.admin_roles === undefined ||
      this.admin_roles === null ||
      this.admin_roles.length === 0
    ) {
      return false;
    }

    let roles = this.admin_roles.filter((item) => {
      return item.role === roleValue;
    });

    return roles.length > 0;
  }
}

export default User;
